import { useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { BlockBody, BlockContainer, BlockHeader } from '@medifind/containers';
import { IconEnvelope, IconMarkerHollow } from '@medifind/icons';
import { IconPhone } from '@medifind/icons';
import { isPrerender } from '@medifind/utils';
import { buildDoctorAddress } from '@medifind/utils';
import { toTitleCase } from '@medifind/utils';
import MFLink from '../MFLink';
import { MFLazyLoad, Skeleton } from '../utils';
import styles from './LocationAccordion.module.scss';

const StaticMap = ({ href, alt, src }) => {
  return (
    <div className={styles['static-map']}>
      <MFLink to={href} target="_blank" rel="noreferrer" analyticsLinkType={'google-maps'}>
        <img
          alt={alt}
          src={src}
          style={{ width: '100%' }}
          loading="lazy"
          onError={(e) => {
            if (!isPrerender) e.target.style.display = 'none';
          }}
        />
      </MFLink>
    </div>
  );
};

const formatTime = (time = '') => {
  const [hrs, mins] = time.split(':');
  const hourInt = parseInt(hrs, 10);

  if (isNaN(hourInt) || isNaN(parseInt(mins, 10))) {
    return null;
  }

  const amOrPm = hourInt >= 12 ? 'PM' : 'AM';
  const formattedHours = hourInt % 12 || 12;
  return `${formattedHours}:${mins} ${amOrPm}`;
};

const LocationHours = ({ hoursOfOperation = {} }) => {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  if (!hoursOfOperation) {
    return null;
  }

  const hoursOfOperationFormatted = days.map((day) => {
    const dayData = { display: toTitleCase(day), value: 'NA' };
    const { open, close, closed, allDay } = hoursOfOperation[day] ?? {};
    if (closed) {
      dayData.value = 'Closed';
    }
    if (allDay) {
      dayData.value = '24 Hours';
    }
    if (open || close) {
      dayData.value = `${formatTime(hoursOfOperation[day]?.open) ?? 'NA'} - ${formatTime(hoursOfOperation[day]?.close) ?? 'NA'}`;
    }
    return dayData;
  });

  // if any data is missing, return null
  if (hoursOfOperationFormatted.some(({ value }) => value === 'NA')) {
    return null;
  }

  return (
    <div className={classNames(styles['hours-of-operations'])}>
      {hoursOfOperationFormatted.map((day, index) => {
        const { display, value } = day ?? {};
        return (
          <div key={`${day}-${index}`} className={classNames(styles['hours-of-operations__section-body'])}>
            <span className={classNames(styles['section-body-text'], styles['display'])}>{display}:</span>
            <span className={styles['section-body-text']}>{value}</span>
          </div>
        );
      })}
    </div>
  );
};

const LocationShelf = ({
  address = {},
  primaryOrgName,
  phone,
  isOtherAddress = false,
  hasData = true,
  hidePartialPhoneNumber = false,
  email,
  classes = {},
}) => {
  const isBadMultipleAddress = hasData && !address?.fidelity && isOtherAddress; // This is temporary and needs to be removed
  const hasMap = hasData && ((address?.fidelity && address?.fidelity < 4) || isBadMultipleAddress) && !isPrerender;
  let staticMapHrefNew;
  let staticMapAltNew;
  let staticMapUrlNew;
  if (hasMap) {
    const { staticMapHref, staticMapAlt, staticMapUrl } = address;
    staticMapHrefNew = staticMapHref;
    staticMapAltNew = staticMapAlt;
    staticMapUrlNew = staticMapUrl;
  }
  const showSkeleton = !hasData && !isOtherAddress && !isPrerender;
  const [displayMap, setDisplayMap] = useState(hasMap && !isOtherAddress);

  if (!primaryOrgName || primaryOrgName?.length === 0) {
    primaryOrgName = address?.orgName;
  }
  if (!phone || phone?.length === 0) {
    phone = address?.orgPhoneNumber;
  }
  // if (!isOtherAddress || isOtherAddress !== true) {
  //   setDisplayMap(true);
  // }

  // isOtherAddress only used on altAddresses (which don't have a fidelity), so show/build map anyway unless otherwise noted

  return (
    <BlockBody className={classNames(styles['locations'], { [styles['other-locations']]: isOtherAddress }, classes.blockBody)}>
      <div className={styles['locations__values-wrapper']}>
        <div>
          {Object.keys(address).length > 0 ? (
            address && Object.keys(address).length > 0 ? (
              <div className={styles['locations__values']}>
                <div className={styles['locations__icon']}>
                  <IconMarkerHollow />
                </div>
                <div className={styles['locations__value']}>
                  {address?.orgName ? (
                    <>
                      {`${address?.orgName}`}
                      <br />
                    </>
                  ) : null}
                  {buildDoctorAddress(address).join(', ')}
                  <LocationHours hoursOfOperation={address?.hoursOfOperation} />
                </div>
              </div>
            ) : (
              <Skeleton />
            )
          ) : (
            'No address information found'
          )}
          {phone ? (
            <div className={classNames(styles['locations__values'], styles['phone-number'])}>
              <div className={styles['locations__icon']}>
                <IconPhone />
              </div>
              <div className={styles['locations__value']}>
                <MFLink href={`tel:${phone}`}>{phone ? phone : <Skeleton />}</MFLink>
              </div>
            </div>
          ) : null}
          {email?.map((e) => (
            <div key={e} className={styles['locations__values']}>
              <div className={styles['locations__icon']}>
                <IconEnvelope width={16} height={16} />
              </div>
              <div className={styles['locations__value']}>
                <MFLink href={`mailto:${e}`}>{e}</MFLink>
              </div>
            </div>
          ))}
        </div>
      </div>
      {(showSkeleton || hasMap) && (
        <>
          {isOtherAddress && staticMapUrlNew && (showSkeleton || hasMap) ? (
            <MFLink onClick={() => setDisplayMap(!displayMap)} className={styles['locations__toggle-map']}>
              {displayMap ? (
                <>
                  <span>Hide Map</span>
                  <span>-</span>
                </>
              ) : (
                <>
                  <span>View Map</span>
                  <span>+</span>
                </>
              )}
            </MFLink>
          ) : null}
          {showSkeleton && <Skeleton height={150} style={{ marginBottom: '10px' }} />}
          {hasMap && (!isOtherAddress || (isOtherAddress && displayMap)) && (
            <MFLazyLoad height={200} noSuspense>
              <StaticMap href={staticMapHrefNew} alt={staticMapAltNew} src={staticMapUrlNew} />
            </MFLazyLoad>
          )}
        </>
      )}
    </BlockBody>
  );
};
LocationShelf.propTypes = {
  address: PropTypes.object,
  primaryOrgName: PropTypes.string,
  phone: PropTypes.string,
  isOtherAddress: PropTypes.bool,
  hasData: PropTypes.bool,
  hidePartialPhoneNumber: PropTypes.bool,
};

const LocationAccordion = ({
  address,
  primaryOrgName,
  phone,
  otherAddresses,
  hasData,
  hidePartialPhoneNumber,
  email,
  style,
  headerPriority,
  classes = {},
}) => {
  return (
    <BlockContainer id="location" className={classes.block}>
      <BlockHeader priority={headerPriority} className={classes.blockHeader}>
        Locations
      </BlockHeader>
      {/* primary location */}
      <LocationShelf
        hasData={hasData}
        address={address}
        primaryOrgName={primaryOrgName}
        phone={phone}
        email={email}
        isOtherAddress={false}
        hidePartialPhoneNumber={hidePartialPhoneNumber}
        classes={classes}
      />

      {otherAddresses?.length > 0 ? (
        <>
          <BlockHeader style={{ backgroundColor: 'transparent' }}>Other Locations</BlockHeader>
          {otherAddresses.map((addr, index) => (
            <LocationShelf
              key={index}
              hasData={hasData}
              address={addr}
              isOtherAddress={true}
              hidePartialPhoneNumber={hidePartialPhoneNumber}
              classes={classes}
            />
          ))}
        </>
      ) : null}
    </BlockContainer>
  );
};
LocationAccordion.propTypes = {
  address: PropTypes.object,
  primaryOrgName: PropTypes.string,
  phone: PropTypes.string,
  otherAddresses: PropTypes.array,
  hasData: PropTypes.bool,
  hidePartialPhoneNumber: PropTypes.bool,
  style: PropTypes.object,
  email: PropTypes.array,
  classes: PropTypes.object,
};
export { LocationAccordion as default, LocationAccordion };
